import { Box, Flex, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { animate, useInView } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useRef, useState } from 'react';

const groupThousands = (n: number) =>
  Math.round(n)
    .toString()
    .replaceAll(/\B(?=(\d{3})+(?!\d))/g, ' ');

type CountUpProps = {
  to: number;
  suffix: string;
  delay: number;
};

const CountUp = ({ to, suffix, delay }: CountUpProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) {
      return;
    }
    const controls = animate(0, to, {
      duration: 1.4,
      delay,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(latest),
    });
    return () => controls.stop();
  }, [inView, to, delay]);

  return (
    <span ref={ref}>
      {groupThousands(display)}
      {suffix}
    </span>
  );
};

type StatProps = {
  label: string;
  delay: number;
  value?: string;
  countTo?: number;
  suffix?: string;
};

const Stat = ({ value, countTo, suffix = '', label, delay }: StatProps) => (
  <Flex
    direction="column"
    alignItems="center"
    gap={1}
    flex={{ base: 1, md: 1 }}
    minW="70px"
    asChild
  >
    <m.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
    >
      <Text
        fontSize={{ base: '2xl', md: '4xl' }}
        fontWeight="900"
        lineHeight="1"
        letterSpacing="-0.02em"
        color="fg"
      >
        {countTo != null ? <CountUp to={countTo} suffix={suffix} delay={delay} /> : value}
      </Text>
      <Text
        fontSize="xs"
        color="fg.muted"
        fontWeight="500"
        textTransform="uppercase"
        letterSpacing="0.08em"
        textAlign="center"
      >
        {label}
      </Text>
    </m.div>
  </Flex>
);

const Divider = () => (
  <Box w="1px" h="40px" bg="whiteAlpha.100" display={{ base: 'none', md: 'block' }} />
);

type TrustStripProps = {
  gamesCount: number;
};

export const TrustStrip = ({ gamesCount }: TrustStripProps) => {
  const { t } = useLingui();
  const gamesTo = gamesCount > 0 ? gamesCount : 20;

  return (
    <Box
      as="section"
      aria-label="Why Teamplay"
      position="relative"
      py={{ base: 6, md: 8 }}
      px={{ base: 4, md: 10 }}
      borderRadius="2xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      background="linear-gradient(135deg, hsla(228, 11.6%, 12%, 0.6) 0%, hsla(228, 11.6%, 10%, 0.4) 100%)"
      style={{ backdropFilter: 'blur(10px)' }}
    >
      <Flex
        direction="row"
        gap={{ base: 4, md: 0 }}
        justifyContent="space-around"
        alignItems="center"
        flexWrap="wrap"
      >
        <Stat countTo={22} suffix="K+" label={t`v3.trust.players`} delay={0} />
        <Divider />
        <Stat countTo={12} suffix="K+" label={t`v3.trust.posts`} delay={0.1} />
        <Divider />
        <Stat countTo={gamesTo} suffix="+" label={t`v3.trust.games`} delay={0.2} />
      </Flex>
    </Box>
  );
};
